import {
  conversationChangedEventSchema,
  socialCommentChangedEventSchema,
  type ConversationChangedEvent,
  type SocialCommentChangedEvent,
} from "@multi-chat/shared";
import { API_BASE_URL } from "./client.js";

const EVENTS_COALESCE_MS = 200;

export type InboxEventsConnection = {
  close: () => void;
};

export type InboxEventsOpenInfo = {
  /** False on the first successful open; true after a later native reconnect. */
  reconnect: boolean;
};

export type InboxEventsHandlers = {
  onConversationChanged: (event: ConversationChangedEvent) => void;
  onSocialCommentChanged?: (event: SocialCommentChangedEvent) => void;
  onOpen?: (info: InboxEventsOpenInfo) => void;
  /** Fires when the transport signals an error/disconnect; recovery happens on the next `onOpen`. */
  onDisconnect?: () => void;
};

/**
 * Opens one credentialed EventSource for an organization.
 * Relies on native EventSource reconnection; does not add a third-party SSE library.
 */
export function openInboxEvents(
  organizationId: number,
  handlers: InboxEventsHandlers,
): InboxEventsConnection {
  const url = `${API_BASE_URL}/organizations/${organizationId}/events`;
  const source = new EventSource(url, { withCredentials: true });
  let hasOpenedBefore = false;
  let closedByClient = false;

  source.onopen = () => {
    if (closedByClient) {
      return;
    }
    const reconnect = hasOpenedBefore;
    hasOpenedBefore = true;
    handlers.onOpen?.({ reconnect });
  };

  source.onerror = () => {
    if (closedByClient) {
      return;
    }
    // Native EventSource will retry. Do not treat error as recovery.
    handlers.onDisconnect?.();
  };

  source.onmessage = (message) => {
    if (closedByClient) {
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(message.data) as unknown;
    } catch {
      return;
    }

    const conversation = conversationChangedEventSchema.safeParse(parsed);
    if (conversation.success) {
      if (conversation.data.organizationId !== organizationId) {
        return;
      }
      handlers.onConversationChanged(conversation.data);
      return;
    }

    const socialComment = socialCommentChangedEventSchema.safeParse(parsed);
    if (socialComment.success) {
      if (socialComment.data.organizationId !== organizationId) {
        return;
      }
      handlers.onSocialCommentChanged?.(socialComment.data);
    }
  };

  return {
    close: () => {
      closedByClient = true;
      source.onopen = null;
      source.onmessage = null;
      source.onerror = null;
      source.close();
    },
  };
}

export { EVENTS_COALESCE_MS };
